import { useState } from 'react'
import { Button } from '@/ui/components/'
import { AlertCircle, Sparkles } from '@/ui/icons'
import { SubscriptionTier } from '@/shared/api/subscriptions'
import { UpgradeModal } from './UpgradeModal'

interface UpgradePromptProps {
  title?: string
  message: string
  feature?: string
  currentTier?: SubscriptionTier
  onUpgrade?: () => void
  variant?: 'default' | 'warning' | 'error'
  className?: string
}

export function UpgradePrompt({
  title,
  message,
  feature,
  currentTier = 'free',
  onUpgrade,
  variant = 'default',
  className = ''
}: UpgradePromptProps) {
  const [modalOpen, setModalOpen] = useState(false)

  const handleUpgrade = () => {
    if (onUpgrade) {
      onUpgrade()
    } else {
      setModalOpen(true)
    }
  }

  const variantStyles = {
    default: 'border-adventure-200 bg-adventure-50',
    warning: 'border-yellow-200 bg-yellow-50',
    error: 'border-red-200 bg-red-50'
  }

  const iconColors = {
    default: 'text-adventure-600',
    warning: 'text-yellow-600',
    error: 'text-red-600'
  }

  const textColors = {
    default: 'text-adventure-800',
    warning: 'text-yellow-800',
    error: 'text-red-800'
  }

  return (
    <>
      <div className={`p-4 rounded-lg border ${variantStyles[variant]} ${className}`}>
        <div className="flex items-start gap-3">
          <AlertCircle className={`h-5 w-5 flex-shrink-0 mt-0.5 ${iconColors[variant]}`} />
          <div className="flex-1 min-w-0">
            <h4 className={`font-semibold ${textColors[variant]} mb-2`}>
              {title || (variant === 'error' ? 'Limit Reached' : 'Upgrade Required')}
            </h4>
            <p className={`text-sm ${textColors[variant]} mb-3`}>{message}</p>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                size="sm"
                onClick={handleUpgrade}
                className="bg-adventure-600 hover:bg-adventure-700 text-white"
              >
                <Sparkles className="h-4 w-4 mr-1" />
                Upgrade to Pro
              </Button>
              {feature && currentTier === 'free' && (
                <span className="text-xs text-muted-foreground">$39.99/year</span>
              )}
            </div>
          </div>
        </div>
      </div>

      <UpgradeModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </>
  )
}
